import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Camera,
  MapPin,
  User,
  Calendar,
  Filter,
  Grid,
  List,
  Download,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Archive,
  ExternalLink,
  AlertTriangle,
  Flame,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { formatUserDateTime, formatSystemDateTime } from '../utils/dateUtils';
import toast from 'react-hot-toast';

const PhotosPage = () => {
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const source = searchParams.get('source') === 'fogos' ? 'fogos' : 'firewatch';
  const isFogos = source === 'fogos';
  const photosPerPage = isFogos ? 30 : 20;

  const filters = {
    fire_id: searchParams.get('fire_id') || '',
    user_id: searchParams.get('user') === 'me' ? user?.id : searchParams.get('user_id') || '',
    limit: photosPerPage,
    offset: (currentPage - 1) * photosPerPage,
  };

  // Fetch FIREWATCH photos (own gallery)
  const { data: fwData, isLoading: fwLoading, refetch: fwRefetch } = useQuery(
    ['photos', filters, currentPage],
    () => apiService.getPhotos(filters),
    { enabled: !isFogos },
  );

  // Fetch fogos.pt public feed
  const { data: fogosData, isLoading: fogosLoading, refetch: fogosRefetch } = useQuery(
    ['fogos-latest', currentPage],
    () => apiService.getFogosLatestPhotos({ page: currentPage, per_page: photosPerPage }),
    { enabled: isFogos, keepPreviousData: true },
  );

  // Unified view of the current source (both shapes normalised for the render loop)
  const view = isFogos
    ? {
        photos: fogosData?.data || [],
        total: fogosData?.meta?.total || 0,
        perPage: fogosData?.meta?.per_page || photosPerPage,
        isLoading: fogosLoading,
      }
    : {
        photos: fwData?.photos || [],
        total: fwData?.total || 0,
        perPage: photosPerPage,
        isLoading: fwLoading,
      };

  const handleSourceChange = (nextSource) => {
    if (nextSource === source) return;
    const next = new URLSearchParams(searchParams);
    if (nextSource === 'fogos') {
      next.set('source', 'fogos');
    } else {
      next.delete('source');
    }
    setSearchParams(next);
    setCurrentPage(1);
    setSelectedPhoto(null);
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta foto?')) return;

    try {
      await apiService.deletePhoto(photoId);
      await fwRefetch();
      toast.success('Foto eliminada com sucesso!');
      setSelectedPhoto(null);
    } catch (error) {
      toast.error('Erro ao eliminar foto');
    }
  };

  const handleDeleteFogosPhoto = async (photoId) => {
    const confirmMsg =
      'Esta acção é IRREVERSÍVEL — a foto será removida permanentemente do fogos.pt (MinIO + BD). Confirma?';
    if (!window.confirm(confirmMsg)) return;

    try {
      await apiService.deleteFogosPhoto(photoId);
      await fogosRefetch();
      toast.success('Foto eliminada do fogos.pt');
      setSelectedPhoto(null);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        toast.error('Foto já não existe no fogos.pt');
        await fogosRefetch();
        setSelectedPhoto(null);
        return;
      }
      if (status === 403) {
        toast.error('Apenas admins podem eliminar do fogos.pt');
        return;
      }
      toast.error('Falha ao contactar o fogos.pt');
    }
  };

  // Pagination calculations
  const totalPages = view.total ? Math.ceil(view.total / view.perPage) : 0;
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBulkDownload = async () => {
    if (!isAdmin()) {
      toast.error('Apenas administradores podem fazer download em massa');
      return;
    }

    setIsDownloading(true);
    try {
      const fireId = filters.fire_id || null;
      await apiService.downloadBulkPhotos(fireId);
      
      const downloadType = fireId ? 'do incêndio' : 'de todas as fotos';
      toast.success(`Download ${downloadType} iniciado com sucesso!`);
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error('Erro ao iniciar download. Tente novamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const PhotoModal = ({ photo, onClose }) => {
    if (!photo) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl max-h-full overflow-auto">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Detalhes da Foto
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Photo */}
              <div>
                <img
                  src={apiService.getPhotoUrl(photo.id)}
                  alt={`Foto de ${photo.fire?.name}`}
                  className="w-full rounded-lg"
                />
              </div>
              
              {/* Details */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Informações
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{photo.fire?.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{photo.user?.name} {photo.user?.surname}</span>
                      {photo.user?.entity && (
                        <span className="text-gray-500 dark:text-gray-400">({photo.user.entity})</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        Tirada em: {formatUserDateTime(photo.taken_at)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Camera className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        Enviada em: {formatSystemDateTime(photo.uploaded_at)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {photo.description && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      Descrição
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {photo.description}
                    </p>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Coordenadas
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {photo.lat.toFixed(6)}, {photo.lng.toFixed(6)}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center space-x-3 pt-4">
                  <a
                    href={apiService.getPhotoUrl(photo.id)}
                    download
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Descarregar</span>
                  </a>
                  
                  {(photo.user_id === user?.id || isAdmin()) && (
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="btn-fire flex items-center space-x-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Eliminar</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PhotoCard = ({ photo }) => (
    <div
      className="relative group cursor-pointer"
      onClick={() => setSelectedPhoto(photo)}
    >
      <img
        src={apiService.getPhotoUrl(photo.id)}
        alt={`Foto de ${photo.fire?.name}`}
        className="w-full h-48 object-cover rounded-lg"
      />
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="w-8 h-8 text-white" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent rounded-b-lg p-3">
        <p className="text-white text-sm font-medium truncate">
          {photo.fire?.location}
        </p>
        <p className="text-gray-300 text-xs">
          {photo.user?.name} {photo.user?.surname}
        </p>
      </div>
    </div>
  );

  const PhotoListItem = ({ photo }) => (
    <div
      className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => setSelectedPhoto(photo)}
    >
      <div className="flex items-center space-x-4">
        <img
          src={apiService.getPhotoUrl(photo.id)}
          alt={`Foto de ${photo.fire?.name}`}
          className="w-16 h-16 object-cover rounded-lg"
        />
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 dark:text-white">
            {photo.fire?.location}
          </h3>
          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
            <span>{photo.user?.name} {photo.user?.surname}</span>
            <span>{new Date(photo.taken_at).toLocaleDateString('pt-PT')}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {new Date(photo.uploaded_at).toLocaleDateString('pt-PT')}
          </p>
        </div>
      </div>
    </div>
  );

  const FogosPhotoCard = ({ photo }) => (
    <div
      className="relative group cursor-pointer"
      onClick={() => setSelectedPhoto(photo)}
    >
      <img
        src={photo.url}
        alt={`Foto do incêndio ${photo.fire_id}`}
        className="w-full h-48 object-cover rounded-lg"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="w-8 h-8 text-white" />
        </div>
      </div>
      <div className="absolute top-2 left-2 flex items-center space-x-1 bg-orange-600/90 text-white text-xs px-2 py-1 rounded">
        <Flame className="w-3 h-3" />
        <span className="font-mono">{photo.fire_id}</span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent rounded-b-lg p-3">
        <p className="text-white text-xs">
          {photo.taken_at ? new Date(photo.taken_at).toLocaleString('pt-PT') : '—'}
        </p>
      </div>
    </div>
  );

  const FogosPhotoListItem = ({ photo }) => (
    <div
      className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => setSelectedPhoto(photo)}
    >
      <div className="flex items-center space-x-4">
        <img
          src={photo.url}
          alt={`Foto do incêndio ${photo.fire_id}`}
          className="w-16 h-16 object-cover rounded-lg"
          loading="lazy"
        />
        <div className="flex-1">
          <h3 className="font-mono text-sm text-gray-900 dark:text-white">
            {photo.fire_id}
          </h3>
          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
            <span>fogos.pt</span>
            {photo.taken_at && (
              <span>{new Date(photo.taken_at).toLocaleString('pt-PT')}</span>
            )}
          </div>
        </div>
        {photo.width && photo.height && (
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {photo.width}×{photo.height}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const FogosPhotoModal = ({ photo, onClose }) => {
    if (!photo) return null;
    const fireDetailUrl = `https://fogos.pt/pt/fogo/${photo.fire_id}/detalhe`;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl max-h-full overflow-auto">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Foto do fogos.pt
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <img src={photo.url} alt={`Foto do incêndio ${photo.fire_id}`} className="w-full rounded-lg" />
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Incidente</h4>
                  <a
                    href={fireDetailUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:underline text-sm"
                  >
                    <Flame className="w-4 h-4" />
                    <span className="font-mono">{photo.fire_id}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Datas</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        Tirada: {photo.taken_at ? new Date(photo.taken_at).toLocaleString('pt-PT') : '—'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Camera className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        Submetida: {photo.created_at ? new Date(photo.created_at).toLocaleString('pt-PT') : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {(photo.width || photo.height) && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Dimensões</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {photo.width} × {photo.height}
                    </p>
                  </div>
                )}

                {photo.gps && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">GPS</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4" />
                        <span>{photo.gps.lat?.toFixed(6)}, {photo.gps.lng?.toFixed(6)}</span>
                      </div>
                      {photo.gps.altitude_m != null && (
                        <div>Altitude: {photo.gps.altitude_m} m</div>
                      )}
                      {photo.gps.heading_deg != null && (
                        <div>Rumo: {photo.gps.heading_deg}°</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3 pt-4">
                  <a
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Descarregar</span>
                  </a>

                  {isAdmin() && (
                    <button
                      onClick={() => handleDeleteFogosPhoto(photo.id)}
                      className="btn-fire flex items-center space-x-2"
                      title="Elimina a foto permanentemente no fogos.pt (irreversível)"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span>Eliminar do fogos.pt</span>
                    </button>
                  )}
                </div>

                {isAdmin() && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Eliminar é irreversível: apaga o objecto no MinIO e a linha na BD do fogos.pt.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Galeria de Fotos
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isFogos
              ? 'Fotos aprovadas no fogos.pt'
              : searchParams.get('user') === 'me'
                ? 'As suas fotos enviadas'
                : 'Fotos de incêndios florestais'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          {/* Source tabs (FIREWATCH / fogos.pt) */}
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md">
            <button
              onClick={() => handleSourceChange('firewatch')}
              className={`px-3 py-2 text-sm rounded-l-md ${
                !isFogos
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              FIREWATCH
            </button>
            <button
              onClick={() => handleSourceChange('fogos')}
              className={`px-3 py-2 text-sm rounded-r-md ${
                isFogos
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Fogos.pt
            </button>
          </div>

          {/* Bulk download for admins (FIREWATCH-only — fogos storage isn't ours) */}
          {!isFogos && isAdmin() && view.total > 0 && (
            <button
              onClick={handleBulkDownload}
              disabled={isDownloading}
              className="btn-primary flex items-center space-x-2"
            >
              {isDownloading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Archive className="w-4 h-4" />
              )}
              <span>
                {isDownloading
                  ? 'A descarregar...'
                  : filters.fire_id
                    ? 'Descarregar Fotos do Incêndio'
                    : 'Descarregar Todas as Fotos'}
              </span>
            </button>
          )}
          
          {/* View mode toggle */}
          <div className="flex items-center space-x-1 border border-gray-300 dark:border-gray-600 rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-l-md ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-r-md ${
                viewMode === 'list'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats and Pagination Info */}
      {view.total > 0 && (
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-500">
                  {view.total}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Total de fotos
                </div>
              </div>
              {totalPages > 1 && (
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                    Página {currentPage} de {totalPages}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Mostrando {Math.min(view.perPage, view.photos.length)} fotos
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photos */}
      {view.isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : view.photos.length > 0 ? (
        <div className={viewMode === 'grid' ? 'photo-grid' : 'space-y-4'}>
          {view.photos.map((photo) => {
            const key = photo.id;
            if (isFogos) {
              return viewMode === 'grid' ? (
                <FogosPhotoCard key={key} photo={photo} />
              ) : (
                <FogosPhotoListItem key={key} photo={photo} />
              );
            }
            return viewMode === 'grid' ? (
              <PhotoCard key={key} photo={photo} />
            ) : (
              <PhotoListItem key={key} photo={photo} />
            );
          })}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nenhuma foto encontrada
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {isFogos
              ? 'Sem fotos aprovadas no feed público do fogos.pt neste momento.'
              : searchParams.get('user') === 'me'
                ? 'Ainda não enviou nenhuma foto.'
                : 'Não há fotos disponíveis no momento.'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-8">
          {/* Previous button */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPrevPage}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              hasPrevPage
                ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Anterior</span>
          </button>

          {/* Page numbers */}
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    pageNum === currentPage
                      ? 'bg-primary-500 text-white'
                      : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          {/* Next button */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNextPage}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              hasNextPage
                ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            <span>Próxima</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Photo Modal */}
      {isFogos ? (
        <FogosPhotoModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      ) : (
        <PhotoModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </div>
  );
};

export default PhotosPage;
